/* eslint-disable */
import React, { useState, useRef } from 'react';
import { useMutation } from '@apollo/client';
import { useLocation } from 'react-router-dom';
import _ from 'lodash';
import { Col, Divider, Popover, Row, Tooltip } from 'antd';
import { SketchPicker } from 'react-color';
import PropTypes from 'prop-types';
import { useProposalContext } from 'context/proposal';
import download from 'downloadjs';
import DocxIcon from 'components/Icons/DocxIcon';
import PdfIcon from 'components/Icons/PdfIcon';
import XlsxIcon from 'components/Icons/XlsxIcon';
import DeleteIcon from 'components/Icons/DeleteIcon';
import DownloadPdfIcon from 'components/Icons/DownloadIcon';
import DownloadIcon from 'components/Icons/DownloadUIIcon';
import FileIcon from 'components/Icons/FileIcon';
import Button from 'components/Button';
import { DELETE_PROPOSAL_ATTACHMENT } from 'graphql/mutations/proposalMutation';
import { PROPOSAL_SUBSCRIPTION_TOPIC } from 'constants/index';

import './AttachmentSection.scss';

const getIcon = (attachment) => {
  if (attachment.type === 'application/pdf') {
    return <PdfIcon />;
  } else if (
    [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ].includes(attachment.type)
  ) {
    return <XlsxIcon />;
  } else if (
    [
      'attachmentapplication/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ].includes(attachment.type)
  ) {
    return <DocxIcon />;
  }
  return <FileIcon />;
};

const AttachmentSection = ({
  showAttachmentModal,
  preview,
  configText,
  setDownloadingFile,
  isTemplate,
  saveSectionBackgroundColor,
  prop: propFromProps,
}) => {
  // Rendered in preview mode from PublishContent, which is also mounted outside the
  // ProposalContextProvider (content library preview, dashboard activity modal), so the
  // context can legitimately be null and the proposal has to come in as a prop instead.
  const {
    propRef,
    setProp,
    proposal: propFromContext,
    lastUsedColor,
    updateLastUsedColor,
    getPresetColors,
  } = useProposalContext() || {};
  const prop = propFromContext || propFromProps;
  const [deleteAttachment] = useMutation(DELETE_PROPOSAL_ATTACHMENT, {
    onCompleted: ({ deleteProposalAttachment }) => {
      const updatedProp = { ...prop, ...deleteProposalAttachment };
      if (propRef) {
        propRef.current = updatedProp;
      }
      setProp?.(updatedProp);
    },
  });

  const [selectedAttachment, setSelectedAttachment] = useState(false);
  const [tempBackgroundColor, setTempBackgroundColor] = useState(null);

  const { pathname } = useLocation();

  const handleBackgroundColorChange = useRef(
    _.debounce((value) => {
      saveSectionBackgroundColor('attachment', value);
    }, 500)
  ).current;

  const ispdf = pathname.indexOf('/PDF/') !== -1;

  const handleDeleteVisibleChange = (visible) => setSelectedAttachment(visible);

  const downloadAttachment = async (attachment) => {
    try {
      setDownloadingFile(true);
      fetch(attachment.url)
        .then((response) => response.blob())
        .then((myBlob) => {
          download(myBlob, attachment.name, attachment.type);
          setDownloadingFile(false);
        });
    } catch (error) {
      download(attachment.url);
      setDownloadingFile(false);
    }
  };

  const removeAttachment = async () => {
    await deleteAttachment({
      variables: {
        proposalId: prop._id,
        index: selectedAttachment,
        topic: `${PROPOSAL_SUBSCRIPTION_TOPIC}_${prop?.channel || prop?.auid || prop?.uid}`,
        type: isTemplate ? 'template' : 'proposal',
      },
    });
    setSelectedAttachment('');
  };

  const backgroundColor =
    tempBackgroundColor ||
    prop?.draft?.sectionStyles?.attachment?.backgroundColor ||
    prop?.draft?.layoutConfig?.backgroundColor ||
    '#ffffff';

  return (
    <div
      className={`attach-section ${preview ? 'attachment-preview' : ''}`}
      style={{ backgroundColor }}>
      {!preview ? (
        <div className="section-buttons">
          <Popover
            placement="topLeft"
            onVisibleChange={(visible) => {
              if (!visible) {
                setTempBackgroundColor(null);
              }
            }}
            content={
              <SketchPicker
                color={backgroundColor}
                onChange={(selectedColor) => {
                  const { hex, rgb } = selectedColor;
                  const color = rgb.a === 1 ? hex : `rgba(${rgb.r},${rgb.g},${rgb.b},${rgb.a})`;
                  setTempBackgroundColor(color);
                  updateLastUsedColor?.(color);
                  handleBackgroundColorChange(color);
                }}
                presetColors={getPresetColors?.()}
              />
            }
            trigger="click"
            overlayClassName="editor-color-picker">
            <span className="background-color-button">
              <span className="background-color-button-text">Background</span>
              <div
                style={{
                  width: '30px',
                  height: '20px',
                  backgroundColor: backgroundColor,
                  border: '1px solid #cbced8',
                  borderRadius: '4px',
                  display: 'inline-block',
                  verticalAlign: 'middle',
                }}
              />
            </span>
          </Popover>
        </div>
      ) : null}
      <div className="attach-header">
        <h3>{configText('attached files', true)}</h3>
        {!preview && (
          <Button className="add-files" text="Add files" onClick={() => showAttachmentModal(true)}>
            Attach Files
          </Button>
        )}
      </div>
      <Row gutter={[16, 16]}>
        {prop?.attachments?.map((attachment, index) => (
          <Col span={8} key={index}>
            {ispdf ? (
              <a href={attachment.url} target="_blank">
                <div className="folder">
                  {getIcon(attachment)}
                  <Tooltip title={attachment.name}>
                    <p>{attachment.name}</p>
                  </Tooltip>
                </div>
              </a>
            ) : (
              <Row className="attachments-options">
                <div className="attachments-options-wrapper">
                  <Tooltip title="Download">
                    <span>
                      <DownloadIconComponent
                        type={attachment.type}
                        className="delete"
                        onClick={() => downloadAttachment(attachment)}
                      />
                    </span>
                  </Tooltip>
                  {!preview && (
                    <Popover
                      content={
                        <Col className="delete-attachment">
                          <h3 className="ant-popover-title">
                            Do you want to delete the attachment?
                          </h3>
                          <Divider />
                          <Row className="button-wrapper">
                            <Button
                              className="medium-btn secondary-btn"
                              text="Delete"
                              onClick={removeAttachment}>
                              Delete
                            </Button>
                            <Button
                              className="medium-btn grey-btn"
                              text="Cancel"
                              onClick={() => handleDeleteVisibleChange(false)}>
                              Cancel
                            </Button>
                          </Row>
                        </Col>
                      }
                      placement="bottomLeft"
                      overlayClassName="Prosprich-editor-components-popover"
                      trigger="click"
                      visible={selectedAttachment === index}
                      onVisibleChange={() => handleDeleteVisibleChange(index)}>
                      <span>
                        <Tooltip title="Delete">
                          <DeleteIcon className="delete-icon" />
                        </Tooltip>
                      </span>
                    </Popover>
                  )}
                </div>
                <div className="folder">
                  {getIcon(attachment)}
                  <Tooltip title={attachment.name}>
                    <p>{attachment.name}</p>
                  </Tooltip>
                </div>
              </Row>
            )}
          </Col>
        ))}

        {!prop?.attachments?.length && (
          <Col span={24}>
            <p className="attachment-empty">Empty</p>
          </Col>
        )}
      </Row>
    </div>
  );
};

AttachmentSection.defaultProps = {
  preview: false,
};

AttachmentSection.propTypes = {
  showAttachmentModal: PropTypes.func,
  preview: PropTypes.bool,
  configText: PropTypes.func.isRequired,
  prop: PropTypes.instanceOf(Object),
};

export default AttachmentSection;

const DownloadIconComponent = ({ type, ...rest }) => {
  if (type === 'application/pdf') {
    return <DownloadPdfIcon {...rest} />;
  }
  return <DownloadIcon {...rest} />;
};
